/**
 * Favorites persistence (NEW feature).
 *
 * A favorite is a self-contained snapshot of a video so the Favorites feed can
 * play it without re-fetching the channel: it stores the already-resolved
 * media URLs alongside light metadata and the originating channel.
 *
 * @typedef {Object} Favorite
 * @property {string} id
 * @property {string} content        - originating channel id
 * @property {string=} title
 * @property {string=} author
 * @property {string=} caption
 * @property {string} playlist       - resolved HLS playlist URL
 * @property {string=} poster        - resolved poster URL
 * @property {string=} avatar        - resolved avatar URL
 * @property {string=} accent        - optional gradient (demo feed)
 * @property {Object=} reactions
 * @property {number} savedAt
 */
import { getJSON, setJSON } from './storage.js';

/**
 * Favorites are stored separately per channel kind ("chill" / "nsfw") so a
 * video saved in one mode never leaks into the other.
 */
const keyFor = (scope) => `favorites:${scope}`;

/** @returns {Favorite[]} newest first */
export function loadFavorites(scope) {
  const list = getJSON(keyFor(scope), []);
  return Array.isArray(list) ? list : [];
}

export function saveFavorites(scope, list) {
  setJSON(keyFor(scope), list);
}

export function isFavorited(list, id) {
  return list.some((f) => f.id === id);
}

/**
 * Pure toggle: returns a new list with the favorite added (front) or removed.
 * @param {Favorite[]} list
 * @param {Favorite} fav
 * @returns {{ list: Favorite[], active: boolean }}
 */
export function toggleFavorite(list, fav) {
  if (isFavorited(list, fav.id)) {
    return { list: list.filter((f) => f.id !== fav.id), active: false };
  }
  return { list: [{ ...fav, savedAt: Date.now() }, ...list], active: true };
}
